/**
 * `runStateStore` — the Weave GUI protocol v1 RunState store.
 *
 * ADDITIVE, ON PURPOSE. `multiAgentStore` has 145 fine-grained selector call
 * sites and is what every shipped screen reads today; this store sits beside it
 * and drives only the v1 components, on runs the server serves the protocol
 * for (`protocol/negotiation.ts`). Nothing here
 * replaces or wraps the legacy store, and neither one writes to the other. When
 * the v1 surface is complete the legacy store is deleted whole — the migration
 * cost lives in that one deletion rather than in a compatibility layer that
 * both halves have to keep true.
 *
 * The store is a SHELL. All the logic is `reduceProtocolFrame`, which is pure
 * and tested against fixture streams; the actions here only decide WHICH
 * reduction to run and what to remember about the transport. Nothing derives
 * state in a component: every read goes through a selector below, each one
 * returning a primitive or an object identity that only changes when its own
 * slice changed, so a `message.part_delta` at 30/s re-renders the transcript
 * and not the action bar.
 */

import { create } from "zustand";
import { registerOrgScopedReset } from "@/lib/org-scope";
import {
  normalizeRunState,
  reduceProtocolFrame,
  type ProtocolFrame,
} from "@/services/multi-agent/protocol/reduce";
import { validateFrame } from "@/services/multi-agent/protocol/validateFrame";
import type {
  Changeset,
  Deploy,
  Error as ProtocolError,
  Evidence,
  Interaction,
  Message,
  OrgContext,
  Part,
  Plan,
  PlanRevision,
  RunState,
  Task,
  Usage,
} from "@/services/multi-agent/protocol";

export type StreamConnection = "idle" | "connecting" | "connected" | "disconnected" | "reconnecting";

export interface RunStateStore {
  /** The run this store is currently holding. */
  runId: string | null;
  /** `null` until the first `state.snapshot`. */
  state: RunState | null;
  connection: StreamConnection;
  /** Highest `seq` seen — the `Last-Event-ID` cursor for a reconnect. */
  lastSeq: number;
  /** A frame could not be applied; the caller owes a `GET /state`. */
  desynced: boolean;
  desyncReason: string | null;
  /** Transport error text, surfaced in the stream chrome. */
  streamError: string | null;
  /** Durable frames applied since attach — diagnostics only. */
  frameCount: number;
  /**
   * The `revision` from the most recent `plan.updated`, or `null`.
   *
   * NOT PART OF RunState, and deliberately kept beside it rather than inside it — same shelf as
   * `connection`, `desynced` and `lastSeq`. §8.2 rule 21 requires a replan to announce itself
   * ("Plan updated (v4): <reason>" with added/changed/removed pills) before the pane repaints, and
   * that "what changed and why" rides the FRAME (`plan.updated.revision`); a `state.snapshot`
   * carries no such field, because a snapshot describes the plan as it is and not how it got there.
   *
   * So it is frame-derived UI state with a defined lifetime: set by a `plan.updated` that carries
   * one, and CLEARED by any snapshot — because after a reconcile this client can no longer claim to
   * know what the last change was. That is the reducer's own rule ("drops it rather than growing a
   * private side-store the next snapshot cannot describe") honoured, not sidestepped: the field lives
   * outside RunState precisely so no snapshot is ever expected to describe it.
   */
  lastPlanRevision: PlanRevision | null;
  /**
   * The reconnect attempt in progress, or `null`.
   *
   * `sse.ts` already bounded the loop at 10 attempts with exponential backoff — none of which was
   * visible. An unbounded "Reconnecting…" gives the reader no way to tell "still trying" from
   * "given up and forgot to say so", and the difference matters most on a long write run where the
   * alternative is refreshing the page mid-deploy.
   */
  reconnectAttempt: { attempt: number; maxAttempts: number } | null;

  attach: (runId: string) => void;
  reset: () => void;
  applyFrame: (frame: ProtocolFrame) => void;
  applySnapshot: (snapshot: unknown) => void;
  setConnection: (connection: StreamConnection) => void;
  /**
   * Which reconnect attempt is in progress, and the cap. `null` when not reconnecting.
   *
   * Cleared by any `setConnection` other than `"reconnecting"` — a counter left standing after the
   * stream came back would tell the reader the app is still struggling when it is not.
   */
  setReconnectAttempt: (info: { attempt: number; maxAttempts: number } | null) => void;
  setStreamError: (message: string | null) => void;
  clearDesync: () => void;
  /** The reader has seen the revision notice. Dismissing it never touches the plan itself. */
  dismissPlanRevision: () => void;
}

const INITIAL = {
  runId: null,
  state: null,
  connection: "idle",
  lastSeq: 0,
  desynced: false,
  desyncReason: null,
  streamError: null,
  frameCount: 0,
  lastPlanRevision: null,
  reconnectAttempt: null,
} satisfies Omit<
  RunStateStore,
  | "attach"
  | "reset"
  | "applyFrame"
  | "applySnapshot"
  | "setConnection"
  | "setReconnectAttempt"
  | "setStreamError"
  | "clearDesync"
  | "dismissPlanRevision"
>;

/** `plan.updated.data.revision`, shape-checked. Anything unrecognisable reads as "no revision". */
function readPlanRevision(data: unknown): PlanRevision | null {
  if (!data || typeof data !== "object") return null;
  const revision = (data as { revision?: unknown }).revision;
  if (!revision || typeof revision !== "object") return null;
  const r = revision as Record<string, unknown>;
  if (typeof r.from_version !== "number") return null;
  return revision as PlanRevision;
}

export const useRunStateStore = create<RunStateStore>((set, get) => ({
  ...INITIAL,

  /**
   * Point the store at a run. Re-attaching to the SAME run is a no-op so a
   * remount (route change, StrictMode double-effect) resumes from the cursor it
   * already has instead of replaying the run from seq 0.
   */
  attach: (runId) => {
    if (get().runId === runId) return;
    set({ ...INITIAL, runId });
  },

  reset: () => set({ ...INITIAL }),

  applyFrame: (frame) => {
    // Guard the dereference below, not just the reducer: a `data:` line that
    // parsed to `null` is still handed to us by the transport.
    if (frame === null || typeof frame !== "object") return;
    const current = get();
    // Tenant/run isolation: a frame from another run can only arrive from a
    // stale stream that outlived its abort, and folding it in would show one
    // run's plan under another's id.
    if (frame.run_id && current.runId && frame.run_id !== current.runId) return;

    /*
     * Development-only contract check, BEFORE the reducer and with no effect on it.
     *
     * The reducer is deliberately tolerant (unknown event ignored, missing field absent), which is
     * right for production and means a genuine violation arrives silently. This makes it loud in
     * development and compiles to nothing in production — see `validateFrame`'s header for why the
     * ajv import cannot reach the bundle. Observation only: the verdict is never acted on, because
     * §1.4 makes the server authoritative and a client refusing frames would diverge from it.
     */
    validateFrame(frame);

    const outcome = reduceProtocolFrame(current.state, frame);
    const seq = typeof frame.seq === "number" ? frame.seq : null;

    /*
     * The revision rides the frame, so it is read HERE rather than in the reducer — which stays pure
     * and stays returning nothing but RunState. A `plan.updated` with a null `revision` is the first
     * version of a plan and has nothing to announce; it must not clear a notice the reader has not
     * seen yet, so only a non-null one writes.
     */
    const revision = frame.event_type === "plan.updated" ? readPlanRevision(frame.data) : null;

    set({
      state: outcome.state,
      ...(revision ? { lastPlanRevision: revision } : {}),
      lastSeq: seq !== null && seq > current.lastSeq ? seq : current.lastSeq,
      // Sticky: cleared by `clearDesync` once the reconcile actually lands, not
      // by the next frame that happened to apply.
      desynced: current.desynced || outcome.desync,
      desyncReason: outcome.desync ? outcome.reason ?? "state.delta could not be applied" : current.desyncReason,
      frameCount: current.frameCount + 1,
    });
  },

  /** A snapshot from `GET /api/runs/{id}/state` — the reconcile path. */
  applySnapshot: (snapshot) => {
    const normalized = normalizeRunState(snapshot);
    const current = get();
    set({
      state: normalized,
      lastSeq: Math.max(current.lastSeq, normalized.run.seq),
      desynced: false,
      desyncReason: null,
      // A snapshot is the plan as it IS. After a reconcile this client cannot claim to know what the
      // last change was, so a stale "Plan updated (v4)" notice must not survive one.
      lastPlanRevision: null,
    });
  },

  setConnection: (connection) =>
    // The counter belongs to a reconnect in progress and nothing else. Reaching `connected` (or
    // `disconnected`, which is where the exhausted loop lands) must retire it, or the status line
    // keeps reporting an attempt that finished.
    set(connection === "reconnecting" ? { connection } : { connection, reconnectAttempt: null }),
  setReconnectAttempt: (reconnectAttempt) => set({ reconnectAttempt }),
  setStreamError: (streamError) => set({ streamError }),
  clearDesync: () => set({ desynced: false, desyncReason: null }),
  dismissPlanRevision: () => set({ lastPlanRevision: null }),
}));

// A user switching organizations must not carry the previous tenant's run into
// the new org's session. Same registration `multiAgentStore` makes.
registerOrgScopedReset("runStateStore", () => useRunStateStore.getState().reset());

// ───────────────────────────────────────────────────────────────────────────
// Selectors
// ───────────────────────────────────────────────────────────────────────────
//
// Every one is a pure `(store) => slice`. The frozen empties below are shared
// singletons, NOT fresh literals: returning `[]` from a selector hands zustand
// a new reference on every render, which is an infinite re-render loop
// ("Maximum update depth exceeded") rather than an empty list. Same rule
// `multiAgentSelectors.ts` records.

const EMPTY_MESSAGES: readonly Message[] = Object.freeze([]);
const EMPTY_EVIDENCE: Readonly<Record<string, Evidence>> = Object.freeze({});
const EMPTY_TASKS: Readonly<Record<string, Task>> = Object.freeze({});
const EMPTY_TASK_ORDER: readonly string[] = Object.freeze([]);
const EMPTY_ERRORS: readonly ProtocolError[] = Object.freeze([]);

/** What the action bar renders before the first snapshot: nothing is legal yet. */
export const NO_ACTIONS: RunState["actions"] = Object.freeze({
  primary: "none",
  available: [],
  disabled: [],
}) as RunState["actions"];

export const selectRunState = (s: RunStateStore): RunState | null => s.state;
/**
 * The whole `run` object.
 *
 * IT CHANGES IDENTITY ON EVERY DURABLE FRAME, and that is not a bug to fix — `bumpSeq` writes
 * `run.seq` on each one, because `seq` is the resume watermark and must advance. So a component
 * selecting this re-renders once per frame: on a streamed answer that is several hundred times,
 * and measurably it dragged the whole typed centre column along with the transcript.
 *
 * PREFER A NARROW SELECTOR BELOW. `selectRunId`, `selectRunMode`, `selectRunStatus`,
 * `selectParentRunId` and `selectResumeFrom` all return primitives or a stable reference, so a
 * `message.part_delta` leaves them alone. Reach for this one only when a consumer genuinely needs
 * several fields at once and re-rendering per frame is acceptable — and say why.
 *
 * `runStateStore.perf.test.ts` asserts both halves: that this churns, and that the narrow ones do
 * not.
 */
export const selectRun = (s: RunStateStore): RunState["run"] | null => s.state?.run ?? null;

/** The run id. A string, so it is stable across every frame of the run. */
export const selectRunId = (s: RunStateStore): string | null => s.state?.run.run_id ?? null;
/** The session this run serves (§2.3) — the key the conversation archive groups runs under. */
export const selectSessionId = (s: RunStateStore): string | null => s.state?.run.session_id ?? null;
/** `read` | `write` | `plan`. Fixed for the life of a run in practice, and stable regardless. */
export const selectRunMode = (s: RunStateStore): RunState["run"]["mode"] | null =>
  s.state?.run.mode ?? null;
export const selectRunLane = (s: RunStateStore): RunState["run"]["lane"] | null =>
  s.state?.run.lane ?? null;
/** The run that spawned this one — the server's own link for the plan → execute journey (§7i). */
export const selectParentRunId = (s: RunStateStore): string | null =>
  s.state?.run.parent_run_id ?? null;
/** The handle for answering an expired question on a successor run (§5.3). */
export const selectResumeFrom = (s: RunStateStore): RunState["run"]["resume_from"] | null =>
  s.state?.run.resume_from ?? null;
export const selectRunStatus = (s: RunStateStore): RunState["run"]["status"] | null =>
  s.state?.run.status ?? null;
/**
 * Can the backend still stop this run? A PRIMITIVE on purpose.
 *
 * `selectRun` changes identity on every durable frame, so a component that read `cancellable` off it
 * would re-render at stream rate — the regression `runStateStore`'s header and `qa/weave.mjs`'s
 * render-count checks exist to prevent. A boolean only re-renders its subscriber when it flips.
 */
export const selectCancellable = (s: RunStateStore): boolean => s.state?.run.cancellable === true;

export const selectRunOutcome = (s: RunStateStore): RunState["run"]["outcome"] | null =>
  s.state?.run.outcome ?? null;
export const selectOrg = (s: RunStateStore): OrgContext | null => s.state?.org ?? null;

/**
 * `actions` is computed by the BACKEND (§3.2) — exactly one `primary` at any
 * moment. The client never infers which button is legal from status +
 * changeset + evidence; that inference is the bug this field exists to kill.
 */
export const selectActions = (s: RunStateStore): RunState["actions"] => s.state?.actions ?? NO_ACTIONS;
export const selectPrimaryAction = (s: RunStateStore): RunState["actions"]["primary"] =>
  s.state?.actions.primary ?? "none";

export const selectPlan = (s: RunStateStore): Plan | null => s.state?.plan ?? null;
/** The un-dismissed revision notice, or `null`. Frame-derived — see the field's own doc. */
export const selectPlanRevision = (s: RunStateStore): PlanRevision | null => s.lastPlanRevision;
export const selectChangeset = (s: RunStateStore): Changeset | null => s.state?.changeset ?? null;
export const selectDeploy = (s: RunStateStore): Deploy | null => s.state?.deploy ?? null;
export const selectEvidence = (s: RunStateStore): Readonly<Record<string, Evidence>> =>
  s.state?.evidence ?? EMPTY_EVIDENCE;
export const selectUsage = (s: RunStateStore): Usage | null => s.state?.usage ?? null;

/**
 * The `tool_call` part for one `tool_call_id`, or `null`.
 *
 * WHY THIS EXISTS. An `approve_tool` interaction carries `context_ref:{kind:"tool_call", id}` and
 * §5.4 wants the card to show the tool and its args. An earlier pass of this work concluded the args
 * were unreachable — that was wrong: `reduceTool` folds `tool.call_start`'s `name`/`display_name`
 * and `tool.call_end`'s `args` into the part the message stream already opened, keyed on `ref`. So
 * the state is there and this is the index into it. No second cache, no second request.
 *
 * Scans messages rather than holding a map: a run has one pending `approve_tool` at a time and the
 * scan is over parts already in memory, so a parallel index would be a second copy of the truth for
 * no measurable gain.
 */
export const selectToolCallPart = (toolCallId: string | null | undefined) => (s: RunStateStore): Part | null => {
  if (!toolCallId) return null;
  for (const message of s.state?.messages ?? []) {
    for (const part of message.parts ?? []) {
      if (part.type !== "tool_call") continue;
      const body = (part.body ?? {}) as { tool_call_id?: unknown };
      if (part.ref === toolCallId || body.tool_call_id === toolCallId) return part;
    }
  }
  return null;
};
export const selectMessages = (s: RunStateStore): readonly Message[] => s.state?.messages ?? EMPTY_MESSAGES;
export const selectTasks = (s: RunStateStore): Readonly<Record<string, Task>> => s.state?.tasks ?? EMPTY_TASKS;
export const selectTaskOrder = (s: RunStateStore): readonly string[] =>
  s.state?.task_order ?? EMPTY_TASK_ORDER;
export const selectErrors = (s: RunStateStore): readonly ProtocolError[] => s.state?.errors ?? EMPTY_ERRORS;

/** The blocking interaction, when one is pending (§5). */
export const selectPendingInteraction = (s: RunStateStore): Interaction | null => {
  const pending = s.state?.interactions.pending;
  if (!pending) return null;
  return s.state?.interactions.byId[pending] ?? null;
};

export const selectConnection = (s: RunStateStore): StreamConnection => s.connection;
export const selectReconnectAttempt = (s: RunStateStore): RunStateStore["reconnectAttempt"] =>
  s.reconnectAttempt;
export const selectIsDesynced = (s: RunStateStore): boolean => s.desynced;
export const selectStreamError = (s: RunStateStore): string | null => s.streamError;
export const selectLastSeq = (s: RunStateStore): number => s.lastSeq;

const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set(["completed", "failed", "cancelled", "expired"]);

/** True once the run can produce no further frames. */
export const selectIsTerminal = (s: RunStateStore): boolean => {
  const status = s.state?.run.status;
  return status ? TERMINAL_RUN_STATUSES.has(status) : false;
};

/** A write run wears different chrome from frame one (§8.2). */
export const selectIsWriteRun = (s: RunStateStore): boolean => s.state?.run.mode === "write";
